/**
 * Copy + error-matching constants for the public join flow
 * (`/join` and `/join/[type]/[token]`).
 *
 * Lives in `appStyles/` because page-level constants for `app/` screens must
 * not live inside `app/` itself — every file there becomes a route.
 */

import { getWcBrand } from '../src/worldcup/helpers/wcBrand.helper';

/**
 * Exact server error message returned when a user is already in another org and
 * tries to join a new one. Matched verbatim to switch the error screen to a
 * "conflict" layout that hides the Try-again button.
 *
 * Source of truth lives in `server/src/routes/accounts/b2bAccounts.router.js`
 * (search for this string). Keep these in sync — a single-word edit on the
 * server will silently break the conflict-detection branch here.
 */
export const ALREADY_IN_ANOTHER_ORG_ERROR =
  'Leave your current account before joining another.';

/**
 * Returned by the server when the org has `allowedEmailDomain` set and the
 * joining user's email doesn't match. Matched verbatim to show a friendly
 * domain-restriction card instead of the raw error string.
 */
export const DOMAIN_NOT_ALLOWED_ERROR = 'domain_not_allowed';

/** Copy for the fallback (no-org-branding) signed-out join screen. */
export const FALLBACK_SIGN_IN_COPY = {
  TITLE: "You've been invited",
  MESSAGE: 'Sign in or create an account to join this organisation.',
} as const;

/** Shared CTA labels used across the public join flow. */
export const JOIN_CTA_LABELS = {
  CREATE_ACCOUNT: 'Create Account',
  SIGN_IN: 'Sign In',
  TRY_AGAIN: 'Try again',
  BACK_TO_APP: 'Back to app',
  ALREADY_HAVE_ACCOUNT_SIGN_IN: 'Already have an account? Sign In',
} as const;

/**
 * Copy for the squad-invite TicketStub variant shown on
 * `/join/group/[token]` when the user is not signed in.
 */
export const getSquadInviteFallbackGroupName = (): string => `${getWcBrand().NAME} Squad`;

export const SQUAD_INVITE_TICKET_COPY = {
  SUBTITLE: 'Predict every match and compete with your squad',
} as const;

/** Format helper for the "Presented by …" line inside the TicketStub. */
export const formatInvitedByLabel = (inviterName: string): string =>
  `Invited by ${inviterName}`;
